import React, { useState, useRef, useLayoutEffect, useState as useReactState } from 'react'
import { ClipboardList, IdCard, AtSign, Phone, Boxes, Clock, ChevronDown, ChevronRight, CalendarDaysIcon, MessageSquareCodeIcon, LucidePaperclip } from 'lucide-react'
import { PurchaseActivityEvent, PurchaseActivityItem } from '@/gen/inventory/common_pb';
import { GetPurchaseOrderResponse } from '@/gen/purchase_order/get_purchase_order_pb';
import { formatNumber } from 'libphonenumber-js';
import { Level } from '@/gen/auth/common_pb';
import { Supplier } from '@/gen/suppliers/common_pb';
import dayjs from 'dayjs';
import { PaymentMethod, PaymentType } from '@/gen/purchase_invoice/common_pb';
import { convertToCurrencyRounded } from '@/utils/currency_converter';

const ReUse1 = ({ icon, name }: { icon: React.ReactNode, name: string }) => (
  <div className='flex items-center gap-x-2 text-xs text-gray-800 font-medium'>
    {icon}
    <span>{name}</span>
  </div>
)

const TimelineCard = ({ name, qty, status, eventType }: { name: string; qty: number; status: string; eventType: string }) => (
  <div className="rounded-md border border-gray-200 px-3 py-2">
    <div className="font-medium">{name}</div>
    <div className="text-xs text-gray-500 mt-2">{
        eventType === 'Payment' ? `Amount: ${convertToCurrencyRounded(qty)}` :
        eventType === 'Fulfillment Status' ? `Received Quantity: ${qty}` :
        eventType === 'Stock Status' ? `Current Quantity: ${qty}` :
        eventType === 'Item Returned' ? `Returned Quantity: ${qty}` :
        eventType === 'Purchase Request' ? `Requested Quantity: ${qty}` :
        eventType === 'Purchase Order' ? `Ordered Quantity: ${qty}` :
        eventType === 'Purchase Invoice' ? `Received Quantity: ${qty}` :
        `Quantity: ${qty}`
      } {status !== "" ? " | " + status : null}</div>
  </div>
)
const TimelineCardEmpth = () => (
  <div className="rounded-md border border-gray-200 px-3 py-2">
    {/* No Items Found */}
    <div className="text-xs text-gray-500">No items yet</div>
    <div className="text-xs text-gray-500 mt-2">
      This activity has no items associated with it.
    </div>
  </div>
)

const TimelineItem = ({ last, cards, title, timestamp, eventType, clickableID, initiatedBy }: { last?: boolean; eventType: string; title: string; timestamp: Date; initiatedBy: Level; clickableID?: string; cards: { name: string; qty: number; status: string }[] }) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [lineHeight, setLineHeight] = useReactState<number | null>(null)
  const [expanded, setExpanded] = useState(false)

  useLayoutEffect(() => {
    if (contentRef.current && !last) {
      setLineHeight(Math.max(0, contentRef.current.offsetHeight - 16))
    }
  }, [last, expanded, cards])

  const visibleCards = expanded ? cards : cards.slice(0, 3)
  const showToggle = cards.length > 3

  return (
    <div className="flex items-start gap-3 relative min-h-[80px] ml-2">
      {/* Icon and vertical line */}
      <div className="relative flex flex-col items-center">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-blue-50 z-10">
          <ClipboardList className="w-3.5 h-3.5 text-blue-600" />
        </span>
        {!last && lineHeight !== null && lineHeight > 0 && (
          <span
            className="absolute left-1/2 top-6 -translate-x-1/2 w-px bg-blue-100"
            style={{ height: `${lineHeight}px` }}
          />
        )}
      </div>
      {/* Content */}
      <div className="flex-1" ref={contentRef}>
        <div className="flex flex-wrap items-center gap-x-1 pt-0.5">
          <span className="text-sm font-medium">{title}</span>
          <span className="text-xs text-gray-600">{cards.length} items</span>
          <span className="bg-gray-100 underline text-gray-700 px-1 rounded ml-1 text-xs" style={{
            cursor: clickableID ? 'pointer' : 'default',
          }} onClick={() => {
            if (clickableID) {
              switch (eventType) {
                case 'Purchase Request':
                  // Open in new tab
                  if (initiatedBy === Level.BRANCH) {
                    window.open(`/branch/purchase-requests/${clickableID}`, '_blank')
                  } else if (initiatedBy === Level.STOCK_POINT) {
                    window.open(`/inventory/purchase-request/${clickableID}`, '_blank')
                  }
                  break;
                case 'Purchase Order':
                  // Open in new tab
                  if (initiatedBy === Level.BRANCH) {
                    window.open(`/branch/purchase-orders/${clickableID}`, '_blank')
                  } else if (initiatedBy === Level.STOCK_POINT) {
                    window.open(`/inventory/purchase-order/${clickableID}`, '_blank')
                  }
                  break;
                case 'Purchase Invoice':
                  // Open in new tab
                  if (initiatedBy === Level.BRANCH) {
                    window.open(`/branch/purchase-invoices/${clickableID}`, '_blank')
                  } else if (initiatedBy === Level.STOCK_POINT) {
                    window.open(`/inventory/purchase-invoice/${clickableID}`, '_blank')
                  }
                  break;
              }
            }
          }}>{eventType}</span>
          <span className="text-xs text-gray-600 ml-1">{
            timestamp.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
            }</span>
        </div>
        {/* Cards under timeline item */}
        <div className="mt-2 flex flex-col gap-2">
          {cards.length === 0 && (
            <TimelineCardEmpth />
          )}
          {visibleCards.map((card, idx) => (
            <TimelineCard key={card.name + idx} name={card.name} qty={card.qty} status={card.status} eventType={eventType} />
          ))}
          {showToggle && (
            <button
              className="text-xs text-gray-500 underline w-fit mt-1 hover:text-gray-700"
              onClick={() => setExpanded((v) => !v)}
              type="button"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const CustomAccordion = ({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) => {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="w-full mb-2">
      <div
        className="flex items-center cursor-pointer select-none"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center bg-gray-100 rounded-md px-2 py-1 text-xs font-medium text-gray-700 border border-gray-300">
          {title}
        </div>
        <div className="flex-1 border-t border-gray-200 mx-2" />
        <span className="text-gray-400">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
      </div>
      {open && <div className="pl-6 py-2 flex flex-col gap-4">{children}</div>}
    </div>
  )
}

const ActivityTab = ({activities, supplierInfo, initiatedBy, productCount, paymentMode, paymentType, dueDate, invoiceNumber}: {
  activities: PurchaseActivityItem[]
  supplierInfo: Supplier,
  initiatedBy: Level,
  productCount: number,
  paymentType?: PaymentType
  paymentMode?: PaymentMethod
  dueDate?: Date,
  invoiceNumber?: string
}) => {

  // Get max and min year from activities
  const years = activities.map(activity => new Date(activity.eventTimestamp?.toDate()!).getFullYear())
  const minYear = Math.min(...years)
  const maxYear = Math.max(...years)
  const timelineData: Record<number, { week: number; title: string; events: {
    eventType: string;
    itemCount: number;
    timestamp: Date;
    clickableID?: string;
    items?: { name: string; qty: number; status: string }[]
  }[] }> = {}
  activities.forEach(activity => {
    const eventTimestamp = activity.eventTimestamp?.toDate()
    if (!eventTimestamp) return

    const week = Math.floor(eventTimestamp.getTime() / (1000 * 60 * 60 * 24 * 7))

    if (!timelineData[week]) {
      timelineData[week] = { week, title: `Week of ${eventTimestamp.toLocaleDateString()}`, events: [] }
    }

    let eventType = 'Unknown Event'
    if (activity.eventType === PurchaseActivityEvent.REQUEST_CREATED) {
      eventType = 'Request Created'
    } else if (activity.eventType === PurchaseActivityEvent.PO_CREATED) {
      eventType = 'PO Created'
    } else if (activity.eventType === PurchaseActivityEvent.GOODS_RECEIVED) {
      eventType = 'Goods Received'
    } else if (activity.eventType === PurchaseActivityEvent.ITEM_RETURNED) {
      eventType = 'Item Returned'
    } else if (activity.eventType === PurchaseActivityEvent.ITEM_FULFILLMENT_STATUS) {
      eventType = 'Item Fulfillment Status'
    } else if (activity.eventType === PurchaseActivityEvent.CURRENT_STOCK_STATUS) {
      eventType = 'Current Stock Status'
    } else if (activity.eventType === PurchaseActivityEvent.PAYMENT_MADE) {
      eventType = 'Payment Made'
    }

    
    const existingEvent = timelineData[week].events.find(e => e.eventType === eventType && e.timestamp.getTime() === eventTimestamp.getTime())
    if (existingEvent) {
      existingEvent.itemCount += activity.items.length || 0
    } else {
      timelineData[week].events.push({
        eventType,
        itemCount: activity.items.length || 0,
        timestamp: eventTimestamp,
        clickableID: activity.relatedItemId,
        items: eventType != "Payment Made" ? activity.items.map(item => {
          let qty = 0
          let status = ''
          if (activity.eventType === PurchaseActivityEvent.ITEM_FULFILLMENT_STATUS) {
              qty = Number(item.receivedQuantity ?? item.orderedQuantity ?? item.requestedQuantity ?? 0)
              status = item.fulfillmentStatus || ''
          } else if (activity.eventType === PurchaseActivityEvent.CURRENT_STOCK_STATUS) {
              qty = Number(item.currentQuantity ?? 0)
              status = item.stockPointName
          } else if (activity.eventType === PurchaseActivityEvent.ITEM_RETURNED) {
              qty = Number(item.quantityReturned ?? 0)
              status = ''
          } else if (activity.eventType === PurchaseActivityEvent.GOODS_RECEIVED) {
              qty = Number(item.quantityReceived ?? 0)
              status = item.quantityCategory || ''
          } else if (activity.eventType === PurchaseActivityEvent.PO_CREATED || activity.eventType === PurchaseActivityEvent.REQUEST_CREATED) {
              qty = Number(item.quantity ?? 0)
              status = ''
          }

          return {
            name: item.item,
            qty: qty,
            status: status
          }
        }) : [
          {
            name: 'Payment Details',
            qty: Number(activity.eventDetails['payment_amount'] ?? 0),
            status: activity.eventDetails['payment_method'] || 'Unknown'
          }
        ]
      })
    }

    // Sort events by timestamp within each week in descending order
    timelineData[week].events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
  })

  const lastValidActivityDate = activities.filter(activity => activity.eventType != PurchaseActivityEvent.CURRENT_STOCK_STATUS && activity.eventType != PurchaseActivityEvent.ITEM_FULFILLMENT_STATUS).sort((a, b) => {
    const dateA = a.eventTimestamp?.toDate()
    const dateB = b.eventTimestamp?.toDate()
    if (!dateA || !dateB) return 0
    return dateB.getTime() - dateA.getTime()
  })[0]?.eventTimestamp?.toDate()


  return (
    <div className="grid grid-cols-5 gap-4 divide-x h-full min-h-0 flex-1">
      <div className="col-span-3 bg-white p-4 flex flex-col h-full min-h-0 overflow-y-auto">
        {/* Activity Title and Year */}
        <div className="mb-2">
          <div className="text-lg font-semibold text-gray-900">Activity</div>
          <div className="text-xs text-gray-500">{
            minYear === maxYear ? `${minYear}` : `${minYear} - ${maxYear}`
            }</div>
        </div>
        {Object.entries(timelineData).sort((a, b) => {
          const weekA = a[1].week
          const weekB = b[1].week
          return weekB - weekA // Sort by week in descending order
        }).map(([week, data]) => (
          <CustomAccordion key={week} title={`Week of ${new Date(data.week * 1000 * 60 * 60 * 24 * 7).toLocaleDateString()}`} defaultOpen>
            {data.events.length > 0 && data.events.map((event, idx) => (
              <TimelineItem
                key={`${week}-${idx}`}
                last={idx === data.events.length - 1}
                title={event.eventType}
                cards={
                  event.items!
                }
                initiatedBy={initiatedBy}
                timestamp={event.timestamp}
                clickableID={event.clickableID}
                eventType={
                  event.eventType === 'Request Created' ? 'Purchase Request' :
                  event.eventType === 'PO Created' ? 'Purchase Order' :
                  event.eventType === 'Goods Received' ? 'Purchase Invoice' :
                  event.eventType === 'Item Returned' ? 'Purchase Return' :
                  event.eventType === 'Item Fulfillment Status' ? 'Fulfillment Status' :
                  event.eventType === 'Current Stock Status' ? 'Stock Status' :
                  event.eventType === 'Payment Made' ? 'Payment' :
                  'Unknown Event'
                }
              />
            ))}
          </CustomAccordion>
        ))}
      </div>
      <div className="col-span-2 bg-white">
        {/* Details grid */}
        <div className='m-2 inline-flex items-center gap-2 px-2 py-1 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-900'>
          <ClipboardList className='w-4 h-4 mr-1' />
          Order Details
        </div>
        <hr className='mb-2' />
        <div className='grid grid-cols-2 gap-4 p-2'>
          <ReUse1 icon={<IdCard className='w-5 h-5' />} name="Supplier Name" />
          <p className='text-xs font-medium'>{supplierInfo.name}</p>
          <ReUse1 icon={<AtSign className='w-4 h-4' />} name="Contact Person" />
          <a href="mailto:robin@neupharm.ae" className='text-xs text-blue-600 underline font-medium'>{supplierInfo.metadata?.contactPersonName}</a>
          <ReUse1 icon={<Phone className='w-4 h-4' />} name="Phone Number" />
          <p className='text-xs font-medium'>{supplierInfo.metadata?.phoneNumber}</p>
          <ReUse1 icon={<Boxes className='w-4 h-4' />} name="No of Products" />
          <p className='text-xs font-medium'>{productCount}</p>
          <ReUse1 icon={<Clock className='w-4 h-4' />} name="Last Activity" />
          <p className='text-xs font-medium'>{
              dayjs(lastValidActivityDate).format('DD MMMM YYYY')
            }</p>
          {invoiceNumber && <ReUse1 icon={<LucidePaperclip className='w-4 h-4' />} name="Invoice Number" />}
          {invoiceNumber && <p className='text-xs font-medium'>{invoiceNumber}</p>}
          {dueDate && <ReUse1 icon={<CalendarDaysIcon className='w-4 h-4' />} name="Due Date" />}
          {dueDate && <p className='text-xs font-medium'>{
              dayjs(dueDate).format('DD MMMM YYYY')
            }</p>}
          {paymentMode && <ReUse1 icon={<MessageSquareCodeIcon className='w-4 h-4' />} name="Payment Mode" />}
          {paymentMode && <p className='text-xs font-medium'>{
              paymentMode === PaymentMethod.CASH ? 'Cash' :
              paymentMode === PaymentMethod.CHEQUE ? 'Cheque' :
              paymentMode === PaymentMethod.BANK_TRANSFER ? 'Bank Transfer' :
              paymentMode === PaymentMethod.CREDIT_CARD ? 'Credit Card' :
              paymentMode === PaymentMethod.MOBILE_PAYMENT ? 'Mobile Payment (UPI, Wallet)' :
              'Unknown'
            }</p>}
          {paymentType && <ReUse1 icon={<MessageSquareCodeIcon className='w-4 h-4' />} name="Payment Type" />}
          {paymentType && <p className='text-xs font-medium'>{
              paymentType === PaymentType.CREDIT ? 'Credit' :
              paymentType === PaymentType.INSTANT ? 'Instant' :
              'Unknown'
            }</p>}
        </div>
      </div>
    </div>
  )
}

export default ActivityTab 